import { useMemo } from 'react';
import { StyleProp, StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';
import { Text } from '@ui-kitten/components';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { getName } from '@pletly/shared';
import { colors, spacing } from '@pletly/ui';
import { UserAvatar } from '%components';
import { RootStackParamList } from '%navigation/types';
import { useUser } from '%hooks';
import { MentionText } from '%components/Mentions/MentionText';
import { OptionsMenu } from '%components/OptionsMenu';
import { Heart as HeartIcon } from '%components/icons/Heart';
import { getTimeToNow } from '%helpers/date';

import { Comment } from '../types';
import { useCommentLikes } from './useCommentLikes';

interface Props {
	comment: Comment;
	onEditComment?: () => void;
	onDeleteComment?: () => void;
	onOptionsPress?: () => void;
	canChangeComment?: boolean;
	style?: StyleProp<ViewStyle>;
}

export const PostComment = ({
	comment,
	onEditComment,
	onDeleteComment,
	onOptionsPress,
	canChangeComment,
	style,
}: Props) => {
	const { navigate } = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

	const user = useUser();
	const isUserComment = comment.author.id === user.id.toString();

	const { disabledLiking, isLikedByUser, likesCount, toggleLike } = useCommentLikes(comment);

	const handleUsernameClick = () => {
		navigate('Profile', { userId: comment.author.id });
	};

	const onLikesAmountClick = () =>
		navigate('Likes', {
			likes: comment.likes,
		});

	const creationTime = useMemo(() => {
		return getTimeToNow(comment.updatedAt);
	}, [comment.updatedAt]);

	return (
		<View style={[styles.container, style]}>
			<View style={styles.rowCenter}>
				<UserAvatar username={getName(comment.author)} src={comment.author.photoUrl} size={32} />
				<Text onPress={handleUsernameClick} style={styles.author} numberOfLines={2}>
					{getName(comment.author)}
				</Text>
				{isUserComment && canChangeComment && (
					<OptionsMenu
						onDelete={onDeleteComment}
						onEdit={onEditComment}
						onOptionsPress={onOptionsPress}
					/>
				)}
			</View>
			<View style={styles.commentContainer}>
				<Text>
					<MentionText text={comment.content} />
				</Text>
			</View>
			<View style={styles.bottomSection}>
				<Text style={styles.bottomSectionText} appearance="hint">
					{creationTime}
				</Text>
				<View style={styles.dot} />
				<View style={styles.rowCenter}>
					<TouchableOpacity onPress={toggleLike} disabled={disabledLiking}>
						<HeartIcon active={isLikedByUser} strokeColor={colors.hint} />
					</TouchableOpacity>
					<TouchableOpacity
						disabled={likesCount === 0 || disabledLiking}
						onPress={onLikesAmountClick}
					>
						<Text style={[styles.bottomSectionText, styles.likesCount]} appearance="hint">
							{likesCount}
						</Text>
					</TouchableOpacity>
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		marginVertical: 8,
		paddingHorizontal: spacing(2),
	},
	rowCenter: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	author: {
		marginLeft: 8,
		flex: 1,
	},
	commentContainer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginTop: 8,
	},
	bottomSection: {
		flexDirection: 'row',
		alignItems: 'center',
		marginTop: 8,
	},
	dot: {
		width: 4,
		height: 4,
		borderRadius: 2,
		backgroundColor: colors.hint,
		marginHorizontal: 12,
	},
	bottomSectionText: {
		fontSize: 14,
	},
	likesCount: {
		marginLeft: 8,
	},
});
